/**
 * Location Autocomplete Module
 * Fetches suggestions from the Geocode add-on's autocomplete ACT endpoint
 * as the user types, displaying a dropdown of matching locations.
 *
 * On form submit, calls detect_country to check if the search matches a known
 * city in a different country. If so (match_type='city'), auto-switches the
 * country dropdown and submits directly. Otherwise lets the template's
 * cross-country geocode detection handle it.
 */

export function initLocationAutocomplete() {
    const inputs = document.querySelectorAll('input[data-target-field="location"]');
    if (!inputs.length) return;

    const actId = window.GEOCODE_AUTOCOMPLETE_ACT;
    if (!actId) return;

    inputs.forEach(input => setupAutocomplete(input, actId));

    // Close all dropdowns when clicking outside
    document.addEventListener('click', (e) => {
        if (!e.target.closest('.location-autocomplete-wrap')) {
            document.querySelectorAll('.location-autocomplete-list').forEach(list => {
                list.hidden = true;
            });
        }
    });
}

/**
 * Intercept form submit to detect country from the search query.
 * If the query matches a known city in a different country, switch
 * the country dropdown before submitting.
 */
function setupCountryDetection(form, input) {
    const detectActId = window.GEOCODE_DETECT_COUNTRY_ACT;
    if (!detectActId) return;

    let isResubmitting = false;

    form.addEventListener('submit', (e) => {
        if (isResubmitting) return; // allow re-submit through

        const query = input.value.trim();
        if (query.length < 2) return;

        const countrySelect = form.querySelector('select[name="country-id"]');
        if (!countrySelect) return;

        e.preventDefault();

        fetch(`/?ACT=${detectActId}&q=${encodeURIComponent(query)}`)
            .then(r => r.json())
            .then(data => {
                // Auto-switch when a different country is detected
                if (data.country_id &&
                    countrySelect.value != data.country_id) {
                    countrySelect.value = data.country_id;
                    // Update flag trigger
                    const option = countrySelect.options[countrySelect.selectedIndex];
                    const iso = option?.dataset.countryCode;
                    if (iso) {
                        const trigger = form.querySelector('.country-select-trigger use');
                        if (trigger) {
                            const href = trigger.getAttribute('href') || '';
                            trigger.setAttribute('href', href.replace(/#.*$/, `#cig--flags--${iso}`));
                        }
                    }
                }
                isResubmitting = true;
                form.submit();
            })
            .catch(() => {
                isResubmitting = true;
                form.submit();
            });
    });
}

function setupAutocomplete(input, actId) {
    let list = null;
    let controller = null;
    let debounceTimer = null;

    // Wrap the input's parent so we can position the dropdown
    const wrap = input.closest('.listing-search-field')
        || input.closest('.home-search-location')
        || input.parentElement;
    wrap.classList.add('location-autocomplete-wrap');

    // Set up country detection on the parent form
    const form = input.closest('form');
    if (form) setupCountryDetection(form, input);

    function getList() {
        if (list) return list;
        list = document.createElement('ul');
        list.className = 'location-autocomplete-list';
        list.hidden = true;
        wrap.appendChild(list);
        return list;
    }

    async function fetchSuggestions(query) {
        if (controller) controller.abort();
        controller = new AbortController();

        try {
            const res = await fetch(`/?ACT=${actId}&q=${encodeURIComponent(query)}&limit=8`, {
                signal: controller.signal
            });
            const data = await res.json();
            controller = null;
            showSuggestions(data.suggestions || []);
        } catch (err) {
            if (err.name !== 'AbortError') {
                console.error('Autocomplete fetch failed:', err);
            }
            controller = null;
        }
    }

    function showSuggestions(suggestions) {
        const ul = getList();
        ul.innerHTML = '';

        if (!suggestions.length) {
            ul.hidden = true;
            return;
        }

        suggestions.forEach(text => {
            const li = document.createElement('li');
            li.textContent = text;
            li.addEventListener('mousedown', (e) => {
                e.preventDefault();
                selectSuggestion(text);
            });
            ul.appendChild(li);
        });

        ul.hidden = false;
    }

    function selectSuggestion(text) {
        input.value = text;
        getList().hidden = true;
        input.focus();
        // Use requestSubmit so the submit event listener (country detection) fires
        if (form) form.requestSubmit();
    }

    // Input handler with debounce
    input.addEventListener('input', () => {
        const query = input.value.trim();

        clearTimeout(debounceTimer);

        if (query.length < 2) {
            getList().hidden = true;
            return;
        }

        debounceTimer = setTimeout(() => fetchSuggestions(query), 200);
    });

    // Keyboard navigation
    input.addEventListener('keydown', (e) => {
        const ul = getList();
        if (ul.hidden) return;

        const items = ul.querySelectorAll('li');
        const active = ul.querySelector('.autocomplete-active');
        const idx = active ? Array.from(items).indexOf(active) : -1;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (active) active.classList.remove('autocomplete-active');
            const next = idx < items.length - 1 ? idx + 1 : 0;
            items[next].classList.add('autocomplete-active');
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (active) active.classList.remove('autocomplete-active');
            const prev = idx > 0 ? idx - 1 : items.length - 1;
            items[prev].classList.add('autocomplete-active');
        } else if (e.key === 'Enter' && active) {
            e.preventDefault();
            selectSuggestion(active.textContent);
        } else if (e.key === 'Escape') {
            ul.hidden = true;
        }
    });

    // Hide on blur (with delay to allow click on suggestion)
    input.addEventListener('blur', () => {
        setTimeout(() => { getList().hidden = true; }, 150);
    });

    // Re-show on focus if there's a query
    input.addEventListener('focus', () => {
        const ul = getList();
        if (ul.children.length && input.value.trim().length >= 2) {
            ul.hidden = false;
        }
    });
}
